// Tectra Pricing Calculator Engine
// All costs and pricing logic based on the pricing model document

// Base costs (what we pay)
export const COSTS = {
  inputTokens: 3, // per million
  outputTokens: 15, // per million
  meetings: 1, // per hour
  transcription: 0.15, // per hour
  integration: 1, // per connection per month
  integrationRecords: 0.002, // per record
  integrationActions: 0.01, // per action
  dedicatedCloudInfra: 600, // fixed monthly cost
};

// Bundle included with each seat
export const BUNDLE_PER_SEAT = {
  inputTokens: 0.5, // million tokens
  outputTokens: 0.15, // million tokens
  meetingHours: 5,
  transcriptionHours: 5,
};

// Calculate cost of bundle per seat
export const BUNDLE_COST_PER_SEAT =
  BUNDLE_PER_SEAT.inputTokens * COSTS.inputTokens +
  BUNDLE_PER_SEAT.outputTokens * COSTS.outputTokens +
  BUNDLE_PER_SEAT.meetingHours * COSTS.meetings +
  BUNDLE_PER_SEAT.transcriptionHours * COSTS.transcription;
// Result: ~$9.50

// Deployment models
export const DEPLOYMENT_MODELS = {
  SHARED_SAAS: {
    name: 'Shared SaaS',
    pricePerSeat: 50,
    minimumSeats: 0,
    setupFee: 500,
    setupFeeWaivedAt: 25,
  },
  DEDICATED_CLOUD: {
    name: 'Dedicated Cloud',
    pricePerSeat: 150,
    minimumSeats: 50,
    setupFee: 2500,
    setupFeeWaivedAt: 100,
  },
};

// Markup banding based on total monthly usage cost
export const MARKUP_BANDS = [
  { maxCost: 5000, markup: 1.0, label: '100% markup (2.0x)' },
  { maxCost: 10000, markup: 0.75, label: '75% markup (1.75x)' },
  { maxCost: Infinity, markup: 0.5, label: '50% markup (1.5x)' },
];

// Contract term discounts
export const CONTRACT_DISCOUNTS = {
  1: 0,
  2: 0.05,
  3: 0.10,
};

// Professional services
export const PROFESSIONAL_SERVICES = {
  standardRate: 200,
  costRate: 100,
};

// Support tiers
export const SUPPORT_TIERS = {
  STANDARD: {
    name: 'Standard Support',
    price: 0,
    percentage: 0,
    cost: 0,
  },
  ENTERPRISE: {
    name: 'Enterprise Support',
    price: null, // Calculated as 30% of MRR
    percentage: 0.30,
    minimumPrice: 500,
    costPercentage: 0.20, // ~20% of the 30% charge goes to actual support costs
  },
};

/**
 * Get the markup percentage based on total usage cost
 */
export function getMarkupBand(totalUsageCost) {
  const band = MARKUP_BANDS.find(band => totalUsageCost <= band.maxCost);
  return band || MARKUP_BANDS[MARKUP_BANDS.length - 1];
}

/**
 * Calculate minimum seats required based on usage
 */
export function calculateRequiredSeats(usage) {
  const seatsForInputTokens = Math.ceil(usage.inputTokens / BUNDLE_PER_SEAT.inputTokens);
  const seatsForOutputTokens = Math.ceil(usage.outputTokens / BUNDLE_PER_SEAT.outputTokens);
  const seatsForMeetings = Math.ceil(usage.meetingHours / BUNDLE_PER_SEAT.meetingHours);

  // Return the maximum seats required across all usage types
  return Math.max(seatsForInputTokens, seatsForOutputTokens, seatsForMeetings, 1);
}

/**
 * Calculate total bundled usage across all seats
 */
export function calculateBundledUsage(seats) {
  return {
    inputTokens: seats * BUNDLE_PER_SEAT.inputTokens,
    outputTokens: seats * BUNDLE_PER_SEAT.outputTokens,
    meetingHours: seats * BUNDLE_PER_SEAT.meetingHours,
    transcriptionHours: seats * BUNDLE_PER_SEAT.transcriptionHours,
  };
}

/**
 * Calculate integration costs
 */
export function calculateIntegrationCost(integrations) {
  return (
    integrations.connections * COSTS.integration +
    integrations.records * COSTS.integrationRecords +
    integrations.actions * COSTS.integrationActions
  );
}

/**
 * Main pricing calculation function
 */
export function calculatePricing(inputs) {
  const {
    deploymentModel,
    usage,
    integrations,
    contractYears,
    discount,
    professionalServices,
    setupFeeWaived,
    manualSeats,
    supportTier,
  } = inputs;

  const model = DEPLOYMENT_MODELS[deploymentModel];

  // Calculate required seats based on usage (no overages allowed)
  const requiredSeats = calculateRequiredSeats(usage);

  // Use manual seats if provided and higher than required, otherwise use required
  const seats = manualSeats ? Math.max(manualSeats, requiredSeats) : requiredSeats;

  // Ensure minimum seats for deployment model
  const finalSeats = Math.max(seats, model.minimumSeats || 0);

  // Calculate bundled usage (all usage is bundled, no overages)
  const bundledUsage = calculateBundledUsage(finalSeats);

  // Calculate integration costs
  const integrationCost = calculateIntegrationCost(integrations);

  // Total usage cost (for banding calculation - integrations only since usage is bundled)
  const bundledCost = finalSeats * BUNDLE_COST_PER_SEAT;
  const totalUsageCost = integrationCost;

  // Get markup band
  const markupBand = getMarkupBand(totalUsageCost);

  // Calculate integration pricing
  const integrationPrice = integrationCost * (1 + markupBand.markup);

  // Seat revenue
  const seatRevenue = finalSeats * model.pricePerSeat;

  // Infrastructure cost (dedicated cloud only)
  const infraCost = deploymentModel === 'DEDICATED_CLOUD' ? COSTS.dedicatedCloudInfra : 0;

  // Subtotal before discounts
  const subtotal = seatRevenue + integrationPrice;

  // Contract term discount
  const contractDiscount = CONTRACT_DISCOUNTS[contractYears] || 0;
  const contractDiscountAmount = subtotal * contractDiscount;

  // Additional discount
  const additionalDiscountAmount = (subtotal - contractDiscountAmount) * (discount / 100);

  // Total after discounts
  const totalAfterDiscounts = subtotal - contractDiscountAmount - additionalDiscountAmount;

  // Professional services
  const professionalServicesRevenue = professionalServices.hours * professionalServices.rate;
  const professionalServicesCost = professionalServices.hours * PROFESSIONAL_SERVICES.costRate;

  // Setup fee
  let setupFee = model.setupFee;
  if (setupFeeWaived || finalSeats >= model.setupFeeWaivedAt || contractYears >= 2) {
    setupFee = 0;
  }

  // Calculate support pricing
  const supportConfig = SUPPORT_TIERS[supportTier] || SUPPORT_TIERS.STANDARD;
  let supportRevenue = 0;
  let supportCost = 0;

  if (supportTier === 'ENTERPRISE') {
    // Calculate as 30% of MRR (before support is added)
    supportRevenue = Math.max(totalAfterDiscounts * supportConfig.percentage, supportConfig.minimumPrice);
    supportCost = supportRevenue * supportConfig.costPercentage;
  }

  // Monthly recurring revenue (includes support)
  const monthlyRecurringRevenue = totalAfterDiscounts + supportRevenue;

  // Annual recurring revenue (ARR)
  const annualRecurringRevenue = monthlyRecurringRevenue * 12;

  // Total revenue (includes one-time fees)
  const totalMonthlyRevenue = monthlyRecurringRevenue + professionalServicesRevenue + setupFee;

  // Total costs
  const totalCosts = bundledCost + integrationCost + infraCost + professionalServicesCost + supportCost;

  // Margin
  const margin = totalMonthlyRevenue - totalCosts;
  const marginPercentage = totalMonthlyRevenue > 0 ? (margin / totalMonthlyRevenue) * 100 : 0;

  // Minimum acceptable price (1.2x cost)
  const minimumPrice = totalCosts * 1.2;

  return {
    // Seat info
    seats: finalSeats,
    requiredSeats,
    manualSeatsAdjustment: manualSeats && manualSeats > requiredSeats,

    // Revenue breakdown
    seatRevenue,
    integrationPrice,
    supportRevenue,
    professionalServicesRevenue,
    setupFee,
    subtotal,
    contractDiscountAmount,
    additionalDiscountAmount,
    totalAfterDiscounts,
    monthlyRecurringRevenue,
    annualRecurringRevenue,
    totalMonthlyRevenue,

    // Cost breakdown
    bundledCost,
    integrationCost,
    supportCost,
    infraCost,
    professionalServicesCost,
    totalCosts,

    // Margin
    margin,
    marginPercentage,
    minimumPrice,

    // Band info
    markupBand,

    // Support info
    supportTier,
    supportConfig,

    // Usage info
    bundledUsage,
    actualUsage: usage,

    // Warnings
    warnings: generateWarnings({
      totalRevenue: totalMonthlyRevenue,
      minimumPrice,
      seats: finalSeats,
      requiredSeats,
      model,
      marginPercentage,
      discount,
      contractDiscount: contractDiscount * 100,
    }),
  };
}

/**
 * Generate warnings and validation messages
 */
function generateWarnings({ totalRevenue, minimumPrice, seats, requiredSeats, model, marginPercentage, discount, contractDiscount }) {
  const warnings = [];
  const errors = [];
  const info = [];

  // Critical: Below minimum margin
  if (totalRevenue < minimumPrice) {
    errors.push({
      type: 'error',
      message: `Price is below minimum acceptable margin (1.2x cost). Minimum price should be $${minimumPrice.toFixed(2)}`,
    });
  }

  // Warning: Low margin
  if (marginPercentage < 25 && marginPercentage >= 20) {
    warnings.push({
      type: 'warning',
      message: `Low margin (${marginPercentage.toFixed(1)}%). Consider reducing discount.`,
    });
  }

  // Error: Below 20% margin
  if (marginPercentage < 20 && totalRevenue >= minimumPrice) {
    errors.push({
      type: 'error',
      message: `Margin is below company minimum (20%). Current: ${marginPercentage.toFixed(1)}%`,
    });
  }

  // Warning: Below minimum seats for dedicated cloud
  if (model.minimumSeats > 0 && seats < model.minimumSeats) {
    warnings.push({
      type: 'warning',
      message: `Below minimum ${model.minimumSeats} seats for ${model.name}. Requires executive approval.`,
    });
  }

  // Info: Seats calculated from usage
  if (seats === requiredSeats) {
    info.push({
      type: 'info',
      message: `${seats} seats required to cover usage (no overages allowed)`,
    });
  }

  // Warning: High discount
  if (discount > 15) {
    warnings.push({
      type: 'warning',
      message: `Discount exceeds 15% guideline. Requires approval.`,
    });
  }

  // Error: Excessive discount
  if (discount > 20) {
    errors.push({
      type: 'error',
      message: `Discount exceeds 20%. Requires C-level approval.`,
    });
  }

  // Info: Contract discount applied
  if (contractDiscount > 0) {
    info.push({
      type: 'info',
      message: `${contractDiscount}% contract term discount applied`,
    });
  }

  // Info: Setup fee waived
  if (model.setupFee > 0 && seats >= model.setupFeeWaivedAt) {
    info.push({
      type: 'info',
      message: `Setup fee waived (${seats} seats ≥ ${model.setupFeeWaivedAt} threshold)`,
    });
  }

  return { errors, warnings, info };
}
